/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React from 'react';
import type { StatusEsteira } from '../api';
import { TONES, TONE_DO_STATUS } from '../paleta';
import { ORDEM_STATUS, ST_META } from '../utils/ordemStatus';
import {
  GRUPOS_FILTRAVEIS_MACRO,
  GRUPOS_MACRO_ORDENADOS,
  ROTULO_MACRO,
  STATUS_DO_GRUPO,
  TOM_DO_GRUPO_MACRO,
  type StatusMacro,
} from '../utils/vocabularioMacro';

/**
 * Filtro de STATUS compartilhado (pedido 04/09: "em todas as telas tem que ter um
 * filtro de status, das empresas que deram sucesso, falha, warning, executando e
 * aguardando (fila)") — 5 pílulas-toggle multi-seleção, na linguagem visual do
 * protótipo (`sistema/index.html`, `.orq-proto`): pílula ATIVA = preenchida com a
 * cor do tom; INATIVA = outline. Nenhuma selecionada = sem filtro (mostra tudo).
 *
 * Rótulos e ordem vêm de ST_META/ORDEM_STATUS e as cores de TONES/TONE_DO_STATUS —
 * fonte única (a pílula "Alerta" herda o nome novo da Fase 8 automaticamente).
 * O "✕" de limpar segue o padrão dos demais filtros e só aparece com seleção.
 */

/** Os 5 status canônicos na ordem de exibição do protótipo (ORDEM_STATUS). */
const STATUS_ORDENADOS = (Object.keys(ORDEM_STATUS) as StatusEsteira[]).sort(
  (a, b) => ORDEM_STATUS[a] - ORDEM_STATUS[b],
);

interface FiltroStatusProps {
  selecionados: StatusEsteira[];
  // eslint-disable-next-line no-unused-vars
  onToggle: (st: StatusEsteira) => void;
  onLimpar: () => void;
  /**
   * `true` na visão MACRO: as pílulas passam a ser os GRUPOS de
   * `vocabularioMacro` — `err + warn` viram "Falha de processamento" e
   * `run + wait` viram "Empresas a processar".
   *
   * Só o VOCABULÁRIO muda; a seleção continua sendo de status canônicos, e
   * marcar um grupo marca todos os status dele. Board e nano seguem com as 6
   * pílulas separadas, que é onde a distinção erro/alerta importa.
   */
  macro?: boolean;
}

/** Uma pílula — o visual é idêntico para status solto e para grupo da macro. */
const Pilula: React.FC<{
  label: string;
  tom: StatusEsteira;
  ativo: boolean;
  title: string;
  onClick: () => void;
}> = ({ label, tom, ativo, title, onClick }) => {
  const t = TONES[TONE_DO_STATUS[tom]];
  // O dot de "Empresas a processar" (idle) é claro demais para texto branco —
  // ativa usa tinta.
  const corAtiva = TONE_DO_STATUS[tom] === 'idle' ? 'oklch(0.3 0.02 165)' : 'oklch(1 0 0)';
  return (
    <div
      onClick={onClick}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 11px',
        borderRadius: '20px',
        border: `1px solid ${t.dot}`,
        background: ativo ? t.dot : 'oklch(1 0 0)',
        color: ativo ? corAtiva : t.fg,
        fontSize: '11.5px',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: ativo ? corAtiva : t.dot,
          flex: 'none',
        }}
      />
      {label}
    </div>
  );
};

/** As pílulas da MACRO: um toggle por GRUPO, marcando/desmarcando os status dele. */
const PilulasMacro: React.FC<{
  selecionados: StatusEsteira[];
  // eslint-disable-next-line no-unused-vars
  onToggle: (st: StatusEsteira) => void;
}> = ({ selecionados, onToggle }) => (
  <>
    {GRUPOS_MACRO_ORDENADOS.filter(g => GRUPOS_FILTRAVEIS_MACRO.includes(g)).map(
      (g: StatusMacro) => {
        const statuses = STATUS_DO_GRUPO[g];
        // ATIVO = TODOS os status do grupo marcados. Um grupo meio-marcado só
        // acontece se alguém descer de nível e voltar, e aí o clique COMPLETA a
        // seleção em vez de zerá-la — é o que o usuário espera de um toggle de
        // grupo que aparece apagado.
        const ativo = statuses.every(st => selecionados.includes(st));
        return (
          <Pilula
            key={g}
            label={ROTULO_MACRO[g]}
            tom={TOM_DO_GRUPO_MACRO[g]}
            ativo={ativo}
            title={
              ativo
                ? `Deixar de filtrar por ${ROTULO_MACRO[g]}`
                : `Mostrar só empresas em ${ROTULO_MACRO[g]}`
            }
            onClick={() => {
              // Marcar o grupo = marcar o que falta; desmarcar = tirar todos.
              statuses.forEach(st => {
                if (ativo || !selecionados.includes(st)) onToggle(st);
              });
            }}
          />
        );
      },
    )}
  </>
);

export const FiltroStatus: React.FC<FiltroStatusProps> = ({
  selecionados,
  onToggle,
  onLimpar,
  macro = false,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
    {macro ? (
      <PilulasMacro selecionados={selecionados} onToggle={onToggle} />
    ) : (
      STATUS_ORDENADOS.map(st => (
        <Pilula
          key={st}
          label={ST_META[st].label}
          tom={st}
          ativo={selecionados.includes(st)}
          title={
            selecionados.includes(st)
              ? `Deixar de filtrar por ${ST_META[st].label}`
              : `Mostrar só empresas em ${ST_META[st].label}`
          }
          onClick={() => onToggle(st)}
        />
      ))
    )}
    {selecionados.length > 0 && (
      <div
        onClick={onLimpar}
        title="Limpar filtro de status"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '26px',
          height: '26px',
          borderRadius: '8px',
          border: '1px solid oklch(0.91 0.006 160)',
          cursor: 'pointer',
          color: 'oklch(0.5 0.015 165)',
          fontSize: '12px',
          flex: 'none',
        }}
      >
        ✕
      </div>
    )}
  </div>
);
